import { useState, useEffect } from "react";
import { Search, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { LocationAutocomplete } from "@/components/ui/location-autocomplete";
import { EquipmentSelect } from "@/components/ui/equipment-select";
import type { TruckSearchTabFilters } from "@/pages/SearchTrucksPageV2";

interface SearchTrucksFilterBarProps {
  onSearch: (params: { origin: string; destination: string; equipmentTypes: string[] }) => void;
  isSearching?: boolean;
  filterValues?: TruckSearchTabFilters;
  onFilterChange?: (filters: TruckSearchTabFilters) => void;
}

export function SearchTrucksFilterBar({ onSearch, isSearching, filterValues, onFilterChange }: SearchTrucksFilterBarProps) {
  const [origin, setOrigin] = useState(filterValues?.origin ?? "");
  const [destination, setDestination] = useState(filterValues?.destination ?? "");
  const [equipmentTypes, setEquipmentTypes] = useState<string[]>(filterValues?.equipmentTypes ?? []);

  // Sync filter changes up to parent for tab persistence
  useEffect(() => {
    onFilterChange?.({
      origin,
      destination,
      equipmentTypes,
      sortBy: filterValues?.sortBy ?? "age_newest",
    });
  }, [origin, destination, equipmentTypes, onFilterChange]);

  return (
    <div className={cn("flex flex-col md:flex-row md:flex-wrap items-stretch md:items-end gap-2 md:gap-0 p-2 md:p-0 rounded-lg border border-border bg-card", "shadow-sm")}>
      <div className="flex flex-col gap-0.5 px-3 py-2 w-full md:w-auto md:min-w-[200px]">
        <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Origin</span>
        <LocationAutocomplete value={origin} onChange={(v) => setOrigin(v)} placeholder="City, State" className="h-7 text-[11px] border-0 bg-transparent p-0 shadow-none focus-visible:ring-0" />
      </div>
      <div className="hidden md:block h-8 w-px bg-border self-center" />
      <div className="flex flex-col gap-0.5 px-3 py-2 w-full md:w-auto md:min-w-[200px]">
        <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Destination</span>
        <LocationAutocomplete value={destination} onChange={(v) => setDestination(v)} placeholder="City, State" className="h-7 text-[11px] border-0 bg-transparent p-0 shadow-none focus-visible:ring-0" />
      </div>
      <div className="hidden md:block h-8 w-px bg-border self-center" />
      <div className="flex flex-col gap-0.5 px-3 py-2 w-full md:w-auto md:min-w-[160px]">
        <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">Equipment Type</span>
        <EquipmentSelect value={equipmentTypes} onChange={(v) => setEquipmentTypes(Array.isArray(v) ? v : [v])} mode="multi" className="h-7 text-[11px] border-0 bg-transparent p-0 shadow-none" />
      </div>
      <div className="hidden md:block flex-1" />
      <div className="px-3 py-2 w-full md:w-auto">
        <Button onClick={() => onSearch({ origin, destination, equipmentTypes })} disabled={isSearching} className="rounded-lg bg-brand-teal text-white font-semibold px-6 hover:bg-brand-teal/90 shrink-0 w-full md:w-auto">
          {isSearching ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Search className="h-4 w-4 mr-2" />}
          Search Trucks
        </Button>
      </div>
    </div>
  );
}
